import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  effect,
} from '@angular/core';
import { ModalService } from '@app/core/services/modal/modal.service';
import { ToolbarService } from '@app/core/services/toolbar/toolbar.service';
import { UiConfigVisibilityEnum } from '@app/generated/graphql';
import {
  PivotTableHeader,
  PivotTableValue,
} from '../../raw-pivot-table/raw-pivot-table.component';
import { Ag1ButtonComponent } from '../../ag1-button/ag1-button.component';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { ModalHostDirective } from '../../../directives/modal-host.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IPivotTableToolbarTrackingContext,
  ToolbarMixpanelEventTrackingService,
} from './toolbar-mixpanel-event-tracking.service';

export interface PivotReport {
  id?: number;
  configType: string;
  name: string;
  rows: PivotTableHeader[];
  cols: PivotTableHeader[];
  values: PivotTableValue[];
  visibility: UiConfigVisibilityEnum;
  renderer: 'table';
  readonly: boolean;
  dirty: boolean;
}

@Component({
  selector: 'ag-toolbar',
  templateUrl: './toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Ag1ButtonComponent,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    ModalHostDirective,
  ],
})
export class ToolbarComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private modalService = inject(ModalService);
  private _toolbarMixpanelTracking = inject(
    ToolbarMixpanelEventTrackingService,
  );
  private toolbarService = inject(ToolbarService);

  readonly configType = input<string>(undefined);

  readonly showCreate = input<boolean>(undefined);

  readonly showSave = input<boolean>(undefined);

  readonly showDuplicate = input<boolean>(undefined);

  readonly showRename = input<boolean>(undefined);

  readonly showDelete = input<boolean>(undefined);

  readonly canSaveAsPublic = input<boolean>(undefined);

  // Internal state (loaded via ToolbarService) — not component inputs
  reports: PivotReport[] = [];
  selectedReport: PivotReport = this.reports[0];

  readonly cols = input<PivotTableHeader[]>([]);
  readonly rows = input<PivotTableHeader[]>([]);
  readonly values = input<PivotTableValue[]>([]);
  readonly data = input<any[]>([]);

  @Output()
  selectedReportChange: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  reportSaved: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  reportDeleted: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  exportClicked: EventEmitter<string> = new EventEmitter();

  constructor() {
    effect(() => {
      const rows = this.rows();
      const cols = this.cols();
      const values = this.values();

      const selectedReport = this.selectedReport;
      if (!selectedReport) {
        return;
      }

      let dirty = false;
      if (selectedReport.rows != rows) {
        selectedReport.rows = rows;
        dirty = true;
      }
      if (selectedReport.cols != cols) {
        selectedReport.cols = cols;
        dirty = true;
      }
      if (selectedReport.values != values) {
        selectedReport.values = values;
        dirty = true;
      }
      if (dirty) {
        selectedReport.dirty = true;
      }
    });
  }

  ngOnInit(): void {
    this.loadConfigs();
  }

  loadConfigs() {
    this.toolbarService
      .load(this.configType())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.reports = res;

        if (this.reports.length > 0) {
          this.selectedReport = this.reports[0];
          this.selectedReportChange.emit(this.selectedReport);
        }
      });
    return;
  }

  handleReportChange(event: PivotReport) {
    this.selectedReportChange.emit(event);
  }

  handleVisibiltyChange(_event: UiConfigVisibilityEnum) {
    this.selectedReport.dirty = true;
  }

  handleCreateClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Create new report',
      field: {
        inputType: 'text',
        inputLabel: 'Report name',
        placeholder: 'enter report name',
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Create report',
      onConfirm: (reportName: string) => {
        const newReport: PivotReport = {
          name: reportName,
          configType: this.configType(),
          cols: [],
          rows: [],
          values: [],
          readonly: false,
          renderer: 'table',
          visibility: UiConfigVisibilityEnum.Private,
          dirty: true,
        };
        this.selectedReport = newReport;
        this.selectedReportChange.emit(this.selectedReport);
        this.reports = [...this.reports, newReport];

        modalInstance.hide();
      },
    });
  }

  handleDeleteClick() {
    const modalInstance = this.modalService.showConfirm({
      content: `This will delete the ${this.selectedReport.name} report, are you sure?`,
      title: 'Delete report?',
      confirmBtnText: 'Delete report',
      confirmBtnClass: 'is-danger',
      onConfirm: () => {
        this.trackAnalytics('Delete');

        if (this.selectedReport.id) {
          modalInstance.isLoading = true;

          this.toolbarService
            .delete(this.selectedReport)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(
              (_res) => {
                this.removeReport();
                modalInstance.isLoading = false;

                modalInstance.hide();
              },
              (err) => {
                console.error(err);
                modalInstance.isLoading = false;
                modalInstance.hide();
              },
            );
        } else {
          this.removeReport();
          modalInstance.hide();
        }
      },
    });
  }

  removeReport() {
    this.reports = this.reports.filter((r) => r != this.selectedReport);
    this.selectedReport = this.reports[0];
    this.selectedReportChange.emit(this.selectedReport);
  }

  createReport() {
    this.toolbarService
      .create(this.selectedReport)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.selectedReport = res;
      });
  }

  updateReport() {
    this.toolbarService
      .update(this.selectedReport)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.selectedReport = res;
      });
  }

  handleSaveClick() {
    if (this.selectedReport.id) {
      this.trackAnalytics('Update');
      this.updateReport();
    } else {
      this.trackAnalytics('Create');
      this.createReport();
    }
  }

  handleDuplicateClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Create Copy',
      field: {
        inputType: 'text',
        inputLabel: 'New report name',
        placeholder: 'enter report name',
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Create Copy',
      onConfirm: (reportName: string) => {
        this.trackAnalytics('Duplicate', {
          new_name: reportName,
        });

        const newReport: PivotReport = {
          ...this.selectedReport,
          id: undefined,
          dirty: true,
          name: reportName,
          visibility: UiConfigVisibilityEnum.Private,
        };

        // select the new report
        this.selectedReport = newReport;

        // add it to the list of available reports
        this.reports = [...this.reports, newReport];

        modalInstance.hide();
      },
    });
  }

  handleRenameClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Rename Report',
      field: {
        inputType: 'text',
        inputLabel: 'Report name',
        placeholder: 'enter report name',
        defaultValue: this.selectedReport.name,
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Rename report',
      onConfirm: (reportName: string) => {
        this.trackAnalytics('Rename', {
          new_name: reportName,
        });

        this.selectedReport.name = reportName;
        this.selectedReport.dirty = true;

        // save it on the backend
        this.updateReport();

        modalInstance.hide();
      },
    });
  }

  handleExportClick() {
    this.exportClicked.emit(this.selectedReport.name);

    this.trackAnalytics('Export');
  }

  trackAnalytics(
    action: 'Create' | 'Update' | 'Delete' | 'Rename' | 'Duplicate' | 'Export',
    extraProperties: { new_name: string } = null,
  ): void {
    if (!this.selectedReport) return;

    const context: IPivotTableToolbarTrackingContext = {
      configType: this.configType(),
      report: this.selectedReport,
    };

    switch (action) {
      case 'Create':
        this._toolbarMixpanelTracking.trackCreateReport(context);
        break;
      case 'Update':
        this._toolbarMixpanelTracking.trackUpdateReport(context);
        break;
      case 'Delete':
        this._toolbarMixpanelTracking.trackDeleteReport(context);
        break;
      case 'Rename':
        this._toolbarMixpanelTracking.trackRenameReport(
          context,
          extraProperties?.new_name,
        );
        break;
      case 'Duplicate':
        this._toolbarMixpanelTracking.trackDuplicateReport(
          context,
          extraProperties?.new_name,
        );
        break;
      case 'Export':
        this._toolbarMixpanelTracking.trackExportReport(context);
        break;
    }
  }
}
